import { Coins, MoreHorizontal, Plus, Scale, ScrollText } from 'lucide-react';
import { useState } from 'react';

import { useUnidadesAdminControllerListar } from '@/api/generated/admin-unidades/admin-unidades';
import type { SaldoDeAmbienteResponse } from '@/api/generated/model';
import {
  ExtratoDialogo,
} from '@/features/admin/unidades/components/ExtratoDialogo';
import {
  LancamentoDialogo,
  type TipoDeLancamento,
} from '@/features/admin/unidades/components/LancamentoDialogo';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const TODOS = 'todos';

/**
 * Crédito de unidades (F6-6).
 *
 * ⚠️ O saldo mostrado é DERIVADO do ledger — não existe campo editável em lugar
 * nenhum desta tela. Toda mudança passa por um lançamento com motivo.
 */
export function UnidadesPage() {
  const { pode } = usePermissoes();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [tipo, setTipo] = useState<string>(TODOS);
  const [lancamento, setLancamento] = useState<{
    tipo: TipoDeLancamento;
    ambiente: SaldoDeAmbienteResponse;
  } | null>(null);
  const [extratoDe, setExtratoDe] = useState<SaldoDeAmbienteResponse | null>(null);

  const { data, isPending, isError, refetch } = useUnidadesAdminControllerListar({
    pagina,
    porPagina: 20,
    ordenarPor: 'nome',
    ordem: 'asc',
    ...(busca.trim() ? { busca: busca.trim() } : {}),
    ...(tipo !== TODOS ? { tipo: tipo as 'INDIVIDUAL' | 'PARCEIRO' } : {}),
  });

  const podeCreditar = pode('admin_unidades.creditar');

  function aoFiltrar(aplicar: () => void) {
    aplicar();
    setPagina(1);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          value={busca}
          onChange={(e) => aoFiltrar(() => setBusca(e.target.value))}
          placeholder="Buscar ambiente por nome ou identificador..."
          className="sm:max-w-xs"
        />

        <Select value={tipo} onValueChange={(v) => aoFiltrar(() => setTipo(v))}>
          <SelectTrigger className="sm:w-40" aria-label="Filtrar por tipo">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODOS}>Todos os tipos</SelectItem>
            <SelectItem value="INDIVIDUAL">Individual</SelectItem>
            <SelectItem value="PARCEIRO">Parceiro</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">
            Saldo de unidades
          </h3>
        </div>

        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={Coins}
            titulo="Nenhum ambiente encontrado"
            descricao={busca ? 'Tente outro termo de busca.' : 'Ainda não há ambientes.'}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ambiente</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                    <TableHead>Último lançamento</TableHead>
                    <TableHead className="w-20">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.itens.map((ambiente) => (
                    <TableRow key={ambiente.tenantId}>
                      <TableCell>
                        <span className="font-medium text-foreground">{ambiente.tenantNome}</span>
                        {!ambiente.tenantAtivo && (
                          <Badge
                            variant="outline"
                            className="ml-2 text-destructive border-destructive/40"
                          >
                            Desativado
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={ambiente.tenantTipo === 'PARCEIRO' ? 'default' : 'outline'}>
                          {ambiente.tenantTipo === 'PARCEIRO' ? 'Parceiro' : 'Individual'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-brand">
                        {ambiente.saldo}
                      </TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">
                        {ambiente.ultimoLancamentoEm
                          ? new Date(ambiente.ultimoLancamentoEm).toLocaleDateString('pt-BR')
                          : 'Nunca movimentou'}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Ações de ${ambiente.tenantNome}`}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setExtratoDe(ambiente)}>
                              <ScrollText className="w-4 h-4" />
                              Extrato
                            </DropdownMenuItem>

                            {podeCreditar && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => setLancamento({ tipo: 'creditar', ambiente })}
                                >
                                  <Plus className="w-4 h-4" />
                                  Creditar
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => setLancamento({ tipo: 'ajustar', ambiente })}
                                >
                                  <Scale className="w-4 h-4" />
                                  Ajustar
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Paginacao
              pagina={data.pagina}
              totalPaginas={data.totalPaginas}
              total={data.total}
              porPagina={data.porPagina}
              aoMudarPagina={setPagina}
            />
          </>
        )}
      </div>

      {lancamento && (
        <LancamentoDialogo
          aberto
          aoFechar={() => setLancamento(null)}
          tipo={lancamento.tipo}
          ambiente={lancamento.ambiente}
        />
      )}

      {extratoDe && (
        <ExtratoDialogo aberto aoFechar={() => setExtratoDe(null)} ambiente={extratoDe} />
      )}
    </div>
  );
}
